import { useEffect, useMemo, useState } from "react";
import { PettyCashOverview } from "components/pettycash/PettyCashOverview";
import { PettyCashAllocations } from "components/pettycash/PettyCashAllocations";
import { PettyCashExpenses } from "components/pettycash/PettyCashExpenses";
import { PettyCashApprovals } from "components/pettycash/PettyCashApprovals";
import { PettyCashReports } from "components/pettycash/PettyCashReports";
import { PettyCashExecute } from "components/pettycash/PettyCashExecute";
import Button from "components/ui-custom/Button";
import { Card, CardContent } from "components/ui-custom/Card";
import {
  LayoutDashboard,
  Users,
  Receipt,
  CheckCircle,
  History,
  Plus,
  Play,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { AddPettyCashDialog } from "components/pettycash/AddPettyCashDialog";
import { PettyCashApi } from "api/petty/service";
import { useAlert } from "components/UI_components/Alert";
import LoadingScreen from "components/UI_components/LoadingScreen";
import { Tabs, TabsList, TabsTrigger } from "components/ui-custom/Tabs";

const pettyCashRuntimeState = {
  orgId: null,
  hasLoadedOnce: false,
  snapshot: null,
};

const getCurrentOrgIdFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}")?.id ?? null;
  } catch (_) {
    return null;
  }
};

const getCachedPettyCashState = () => {
  const currentOrgId = getCurrentOrgIdFromStorage();
  const sameOrg =
    pettyCashRuntimeState.orgId != null &&
    String(pettyCashRuntimeState.orgId) === String(currentOrgId);

  if (!pettyCashRuntimeState.hasLoadedOnce || !sameOrg) {
    return null;
  }

  return pettyCashRuntimeState.snapshot;
};

const OwnerTabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "team", label: "Team", icon: Users },
  // { id: "submit", label: "Submit", icon: Receipt },
  { id: "approvals", label: "Approvals", icon: CheckCircle },
  // { id: "history", label: "History", icon: History },
];

const EmployeeTabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "execute", label: "Execute", icon: Play },
  // { id: "history", label: "History", icon: History },
];

export default function PettyCashManagement() {
  const isOwner = localStorage.getItem("isOwner") === "true";
  const { addAlert } = useAlert();
  const cachedPettyCashState = useMemo(() => getCachedPettyCashState(), []);
  const [activeTab, setActiveTab] = useState("overview");
  const [pettyCash, setPettyCash] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [employees] = useState([]);
  const [isAddPettyCashOpen, setIsAddPettyCashOpen] = useState(false);
  const [isCreateWalletOpen, setIsCreateWalletOpen] = useState(false);

  const [walletExists, setWalletExists] = useState(
    cachedPettyCashState?.walletExists ?? null,
  ); // null = unknown, boolean otherwise
  const [isWalletBlocked, setIsWalletBlocked] = useState(
    cachedPettyCashState?.isWalletBlocked ?? false,
  );
  const [blockedMessage, setBlockedMessage] = useState(
    cachedPettyCashState?.blockedMessage ?? "",
  );
  const [walletLoading, setWalletLoading] = useState(false);
  const [overview, setOverview] = useState(cachedPettyCashState?.overview ?? null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [teamAllocations, setTeamAllocations] = useState(
    cachedPettyCashState?.teamAllocations ?? null,
  );
  const [teamAllocationsLoading, setTeamAllocationsLoading] = useState(false);
  const [approvals, setApprovals] = useState(cachedPettyCashState?.approvals ?? []);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsLoaded, setApprovalsLoaded] = useState(
    cachedPettyCashState?.approvalsLoaded ?? false,
  );
  const [approvalsCount, setApprovalsCount] = useState(
    cachedPettyCashState?.approvalsCount ?? 0,
  );
  const [approvalActionLoadingById, setApprovalActionLoadingById] = useState(
    {},
  );
  const [myRequestedApprovals, setMyRequestedApprovals] = useState(
    cachedPettyCashState?.myRequestedApprovals ?? [],
  );
  const [myRequestedApprovalsLoading, setMyRequestedApprovalsLoading] =
    useState(false);
  const [executeLoadingById, setExecuteLoadingById] = useState({});
  const [isRefreshingView, setIsRefreshingView] = useState(false);

  const orgId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("organization") || "{}")?.id;
    } catch (_) {
      return null;
    }
  }, []);

  const loadWalletAndOverview = async () => {
    if (!orgId) {
      setWalletExists(false);
      setOverview(null);
      return;
    }

    setWalletLoading(true);
    setIsWalletBlocked(false);
    setBlockedMessage("");

    try {
      if (isOwner) {
        const walletRes = await PettyCashApi.getWallet(orgId);
        const data = walletRes?.data ?? walletRes;
        const exists =
          (walletRes?.status ?? true) &&
          data != null &&
          (Array.isArray(data)
            ? data.length > 0
            : Object.keys(data || {}).length > 0);
        setWalletExists(Boolean(exists));

        if (exists) {
          setOverviewLoading(true);
          try {
            const overviewRes = await PettyCashApi.getOverview(orgId);
            setOverview(overviewRes?.data ?? null);
          } finally {
            setOverviewLoading(false);
          }
        } else {
          setOverview(null);
        }
      } else {
        // Employee flow
        const walletRes = await PettyCashApi.getMyWalletOverview(orgId);
        if (walletRes?.status === false) {
          setWalletExists(false);
          setIsWalletBlocked(true);
          setBlockedMessage(
            walletRes?.message ||
              "Petty cash wallet not found for this employee",
          );
          setOverview(null);
        } else {
          setWalletExists(true);
          setIsWalletBlocked(false);
          setOverview(walletRes?.data || null);
        }
      }
    } catch (err) {
      // If wallet doesn't exist yet, backend may respond 404/400 — treat as "no wallet"
      if (err?.status === 404) {
        setWalletExists(false);
        setOverview(null);
      } else {
        console.error("Failed to load petty cash wallet/overview", err);
        setWalletExists(false);
        setOverview(null);
      }
    } finally {
      setWalletLoading(false);
    }
  };

  const loadTeamAllocations = async () => {
    if (!orgId) {
      setTeamAllocations(null);
      return;
    }
    setTeamAllocationsLoading(true);
    try {
      const res = await PettyCashApi.getTeamAllocations(orgId);
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setTeamAllocations(list);
    } catch (e) {
      console.error("Failed to load team allocations", e);
      setTeamAllocations([]);
    } finally {
      setTeamAllocationsLoading(false);
    }
  };

  const loadApprovals = async () => {
    if (!orgId) {
      setApprovals([]);
      setApprovalsLoaded(false);
      setApprovalsCount(0);
      return;
    }

    setApprovalsLoading(true);
    try {
      const res = await PettyCashApi.getApprovals(orgId);
      const payload = res?.data ?? res;

      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      setApprovals(list);
      const pendingCount = list.reduce((acc, item) => {
        const statusType = String(item?.status_type || "").toUpperCase();
        return acc + (statusType === "PENDING" ? 1 : 0);
      }, 0);
      setApprovalsCount(pendingCount);
      setApprovalsLoaded(true);
    } catch (e) {
      console.error("Failed to load petty cash approvals", e);
      setApprovals([]);
      setApprovalsCount(0);
      setApprovalsLoaded(true);
    } finally {
      setApprovalsLoading(false);
    }
  };

  const loadMyRequestedApprovals = async (force = false) => {
    if (!orgId || (!walletExists && !force)) {
      setMyRequestedApprovals([]);
      return;
    }

    setMyRequestedApprovalsLoading(true);
    try {
      const res = await PettyCashApi.getMyRequestedApprovals(orgId);
      const payload = res?.data ?? res;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setMyRequestedApprovals(list);
    } catch (e) {
      console.error("Failed to load my requested approvals", e);
      setMyRequestedApprovals([]);
    } finally {
      setMyRequestedApprovalsLoading(false);
    }
  };

  const handleRefreshView = async () => {
    if (isRefreshingView) return;
    setIsRefreshingView(true);
    try {
      await loadWalletAndOverview();
      const requests = [loadTeamAllocations(), loadApprovals()];
      if (!isOwner) requests.push(loadMyRequestedApprovals(true));
      await Promise.all(requests);
    } catch (e) {
      addAlert(e?.message || "Failed to refresh petty cash data", "error");
    } finally {
      setIsRefreshingView(false);
    }
  };

  const handleExecutePayment = async (approvalId, category) => {
    if (!orgId || approvalId == null) return;

    setExecuteLoadingById((prev) => ({ ...prev, [approvalId]: true }));
    try {
      await PettyCashApi.executeTransactionsFromApproval(orgId, approvalId);

      setMyRequestedApprovals((prev) =>
        prev.map((item) => {
          const id = item?.approval_id ?? item?.id;
          return id === approvalId ? { ...item, is_executed: true } : item;
        }),
      );
    } catch (e) {
      console.error("Failed to execute approval transaction", e);
      addAlert(e?.message || "Failed to execute approval transaction", "error");
    } finally {
      setExecuteLoadingById((prev) => {
        const next = { ...prev };
        delete next[approvalId];
        return next;
      });
    }
  };

  useEffect(() => {
    loadWalletAndOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (walletExists) {
      loadTeamAllocations();
      loadApprovals();
      if (!isOwner) loadMyRequestedApprovals();
    } else {
      setTeamAllocations(null);
      setApprovals([]);
      setApprovalsLoaded(false);
      setApprovalsCount(0);
      setMyRequestedApprovals([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletExists, orgId, isOwner]);

  useEffect(() => {
    if (walletExists === null && !isWalletBlocked) return;

    pettyCashRuntimeState.hasLoadedOnce = true;
    pettyCashRuntimeState.orgId = orgId ?? null;
    pettyCashRuntimeState.snapshot = {
      walletExists,
      isWalletBlocked,
      blockedMessage,
      overview,
      teamAllocations,
      approvals,
      approvalsLoaded,
      approvalsCount,
      myRequestedApprovals,
    };
  }, [
    orgId,
    walletExists,
    isWalletBlocked,
    blockedMessage,
    overview,
    teamAllocations,
    approvals,
    approvalsLoaded,
    approvalsCount,
    myRequestedApprovals,
  ]);

  const totalPettyCash =
    walletExists && typeof overview?.total_petty_cash === "number"
      ? overview.total_petty_cash
      : 0;
  const totalAllocated =
    walletExists &&
    typeof (overview?.total_allocated ?? overview?.total_allocated) === "number"
      ? (overview.total_allocated ?? overview.total_allocated)
      : 0;
  const balanceLeft =
    walletExists &&
    typeof (overview?.total_balance_left ?? overview?.total_balance) ===
      "number"
      ? (overview.total_balance_left ?? overview.total_balance)
      : 0;
  const totalSpent = isOwner
    ? walletExists &&
      typeof overview?.total_petty_cash === "number" &&
      typeof overview?.total_balance_left === "number"
      ? overview.total_petty_cash - overview.total_balance_left
      : 0
    : overview?.amount_spent || 0;

  const spendableBalance = overview?.spendable_balance || 0;

  const handleCreateWallet = async ({ amount }) => {
    if (!orgId) return;
    await PettyCashApi.createWallet(orgId, amount);
    setIsCreateWalletOpen(false);
    await loadWalletAndOverview();
  };

  const handleTopUp = async ({ amount, notes }) => {
    if (!orgId) return;
    await PettyCashApi.credit(orgId, amount, notes || "");
    setIsAddPettyCashOpen(false);
    await loadWalletAndOverview();
  };

  const handleAllocateFunds = async ({
    employeeId,
    amount,
    spendableAmount,
    sourceType,
    remarks,
  }) => {
    if (!orgId) return;
    await PettyCashApi.transfer(orgId, {
      employee: Number(employeeId),
      transfer_amount: Number(amount),
      spendable_balance: Number(spendableAmount),
      source_type: String(sourceType),
      ...(remarks ? { remarks: String(remarks) } : {}),
    });
    await loadWalletAndOverview();
    await loadTeamAllocations();
  };

  const handleUpdateSpendableLimit = async (employeeId, spendableBalance) => {
    if (!orgId) return;
    await PettyCashApi.updateSpendableLimit(orgId, employeeId, spendableBalance);
    await loadTeamAllocations();
    addAlert("Spendable limit updated successfully", "success");
  };

  const handleAddExpense = (expense) => {
    const needsApproval = expense.amount > 2000;

    const newExpense = {
      ...expense,
      id: `EX${String(expenses.length + 1).padStart(3, "0")}`,
      status: needsApproval ? "Pending" : "Approved",
    };
    setExpenses([newExpense, ...expenses]);
  };

  const handleApproveExpense = async (expenseId) => {
    if (approvalsLoaded) {
      if (!orgId) return;
      if (approvalActionLoadingById?.[expenseId]) return;

      setApprovalActionLoadingById((prev) => ({
        ...(prev || {}),
        [expenseId]: true,
      }));
      try {
        await PettyCashApi.actionApproval(orgId, expenseId, "APPROVED", "");
        setApprovals((prev) =>
          (Array.isArray(prev) ? prev : []).filter((a) => a?.id !== expenseId),
        );
        setApprovalsCount((c) => Math.max(0, Number(c || 0) - 1));
      } catch (e) {
        console.error("Failed to approve petty cash approval", e);
      } finally {
        setApprovalActionLoadingById((prev) => ({
          ...(prev || {}),
          [expenseId]: false,
        }));
        await loadApprovals();
      }
      return;
    }

    setExpenses(
      expenses.map((e) =>
        e.id === expenseId ? { ...e, status: "Approved" } : e,
      ),
    );
  };

  const handleRejectExpense = async (expenseId) => {
    if (approvalsLoaded) {
      if (!orgId) return;
      if (approvalActionLoadingById?.[expenseId]) return;

      setApprovalActionLoadingById((prev) => ({
        ...(prev || {}),
        [expenseId]: true,
      }));
      try {
        await PettyCashApi.actionApproval(orgId, expenseId, "REJECTED", "");
        setApprovals((prev) =>
          (Array.isArray(prev) ? prev : []).filter((a) => a?.id !== expenseId),
        );
        setApprovalsCount((c) => Math.max(0, Number(c || 0) - 1));
      } catch (e) {
        console.error("Failed to reject petty cash approval", e);
      } finally {
        setApprovalActionLoadingById((prev) => ({
          ...(prev || {}),
          [expenseId]: false,
        }));
        await loadApprovals();
      }
      return;
    }

    setExpenses(
      expenses.map((e) =>
        e.id === expenseId ? { ...e, status: "Rejected" } : e,
      ),
    );
  };

  const handleApproveAllocation = (allocationId) => {
    setAllocations(
      allocations.map((a) =>
        a.id === allocationId ? { ...a, status: "Approved" } : a,
      ),
    );
  };

  const handleRejectAllocation = (allocationId) => {
    setAllocations(
      allocations.map((a) =>
        a.id === allocationId ? { ...a, status: "Rejected" } : a,
      ),
    );
  };

  const employeeBalances = useMemo(() => {
    if (Array.isArray(teamAllocations) && teamAllocations.length > 0) {
      return teamAllocations
        .map((t) => ({
          id: String(t.employee_id ?? t.employeeId ?? ""),
          name: t.employee_name || "Unknown",
          role: t.employee_profession || "",
          project: t.project || "",
          totalAllocated: Number(t.total_allocated || 0),
          totalSpent: 0, // keep hardcoded for now
          balance: Number(t.balance || 0),
          spendableAmount: Number(
            t.spendable_amount ?? t.spendable_balance ?? t.spendableAmount ?? 0,
          ),
          userpic: t.userpic || null,
        }))
        .filter((e) => e.id);
    }
    return [];
  }, [teamAllocations]);

  const allApprovalsMapped = useMemo(() => {
    if (!walletExists) return [];

    const parseRequestedByName = (requestedBy) => {
      if (!requestedBy) return "Unknown";
      const str = String(requestedBy);
      const parts = str
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
      return parts.length >= 2 ? parts.slice(1).join(" | ") : str;
    };

    const safeNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const userpicByEmployeeName = new Map(
      (Array.isArray(teamAllocations) ? teamAllocations : [])
        .filter((t) => t?.employee_name && t?.userpic)
        .map((t) => [String(t.employee_name).trim(), t.userpic]),
    );

    const toExpenseLike = (a) => {
      const tx = a?.transaction_payload || {};
      const receiptUrl =
        a?.receipt ||
        a?.attachment ||
        tx?.receipt ||
        tx?.attachment ||
        null;

      const title =
        tx?.title || tx?.purpose || a?.payment_type || a?.category || "Expense";

      const date =
        tx?.date ||
        tx?.transaction_date ||
        tx?.created_on ||
        tx?.po_data?.order_date ||
        a?.created_on ||
        new Date().toISOString();

      const project =
        a?.site_name ||
        (a?.site != null ? `Site #${a.site}` : "Organization Purchase");
      const employeeName = parseRequestedByName(a?.requested_by);
      const userpic =
        a?.userpic ||
        a?.requested_by_userpic ||
        tx?.userpic ||
        tx?.requested_by_userpic ||
        userpicByEmployeeName.get(employeeName) ||
        null;

      return {
        id: a?.id,
        employeeName,
        title: String(title),
        amount: safeNumber(a?.amount),
        project: String(project),
        date,
        statusType: String(a?.status_type || "").toUpperCase(),
        category: a?.category || "",
        paymentType: a?.payment_type || "",
        attachment: receiptUrl,
        receipt: receiptUrl,
        remarks: a?.remarks || "",
        userpic,
        _raw: a,
      };
    };

    if (approvalsLoaded) {
      return (Array.isArray(approvals) ? approvals : [])
        .map(toExpenseLike)
        .filter((e) => e.id != null);
    }

    return expenses.map((e) => ({
      ...e,
      employeeName: e.employeeName || "Unknown",
      userpic: e.userpic || null,
      statusType: String(e.status || "").toUpperCase(),
      date: e.date || new Date().toISOString(),
    }));
  }, [walletExists, approvalsLoaded, approvals, expenses, teamAllocations]);

  const pendingExpenses = useMemo(() => {
    return allApprovalsMapped.filter((a) => a.statusType === "PENDING");
  }, [allApprovalsMapped]);

  const pendingAllocations = walletExists
    ? allocations.filter((a) => a.status === "Pending")
    : [];
  const hasLoadedOnceForOrg =
    pettyCashRuntimeState.hasLoadedOnce &&
    String(pettyCashRuntimeState.orgId) === String(orgId);
  const pageLoading =
    walletExists === null && !isWalletBlocked && !hasLoadedOnceForOrg;

  return (
    <LoadingScreen loading={pageLoading}>
      <div className="flex flex-col">
        {/* Header */}
        <div className="px-10 pt-8 pb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Petty Cash
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage your project expenses with ease
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`h-10 w-10 flex items-center justify-center border border-gray-300 rounded-md transition-colors ${isRefreshingView ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"}`}
              onClick={handleRefreshView}
              title="Refresh"
              aria-label="Refresh"
              disabled={isRefreshingView || !orgId}
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshingView ? "animate-spin" : ""}`}
              />
            </button>
            <Button
              onClick={() => {
                if (walletExists) setIsAddPettyCashOpen(true);
                else setIsCreateWalletOpen(true);
              }}
              variant="primary"
              disabled={
                walletLoading ||
                overviewLoading ||
                !orgId ||
                (!isOwner && isWalletBlocked)
              }
              className={!isOwner ? "hidden" : ""}
            >
              <Plus className="w-4 h-4 mr-2" />
              {walletLoading
                ? "Loading..."
                : walletExists
                  ? "Add Petty Cash"
                  : "Create Wallet"}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="px-10 flex-1 overflow-y-auto">
          {isWalletBlocked ? (
            <div className="py-12 flex items-center justify-center">
              <Card className="max-w-lg w-full">
                <CardContent className="p-8 text-center flex flex-col items-center">
                  <div className="w-16 h-16 bg-sm-accent/10 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-sm-accent" />
                  </div>
                  <h3 className="text-xl font-bold text-primary-text mb-2">
                    Features Blocked
                  </h3>
                  <p className="text-text-gray mb-6">
                    {blockedMessage ||
                      "You currently don't have any wallet configured for petty cash. This feature will be accesible when you are given petty cash allocation by your organization admin."}
                  </p>
                  <div className="w-full h-px bg-sm-border mb-6"></div>
                  <p className="text-xs text-text-gray italic">
                    Please contact your organization administrator for more
                    details.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : !walletExists ? (
            <div className="py-4 text-sm text-muted-foreground">
              No petty cash wallet created yet.
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="h-9 p-1">
                    {(isOwner ? OwnerTabs : EmployeeTabs).map((tab) => {
                      const Icon = tab.icon;
                      const approvalsPendingCount = approvalsLoaded
                        ? approvalsCount
                        : pendingExpenses.length;
                      const pendingCount =
                        tab.id === "approvals"
                          ? approvalsPendingCount + pendingAllocations.length
                          : tab.id === "execute"
                            ? myRequestedApprovals.filter(
                                (a) => a.status === "APPROVED" && !a.is_executed,
                              ).length
                            : 0;

                      return (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="h-7 text-sm px-4 gap-1.5 relative"
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {tab.label}
                          {pendingCount > 0 && (
                            <span className="absolute top-1 -right-1 w-5 h-5 bg-sm-accent text-white text-xs rounded-full flex items-center justify-center font-semibold">
                              {pendingCount}
                            </span>
                          )}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto py-4">
                {activeTab === "overview" && (
                  <PettyCashOverview
                    isOwner={isOwner}
                    totalPettyCash={totalPettyCash}
                    totalAllocated={totalAllocated}
                    totalSpent={totalSpent}
                    balanceLeft={balanceLeft}
                    spendableBalance={spendableBalance}
                    pendingExpenses={pendingExpenses}
                    myRecentApprovals={myRequestedApprovals.slice(0, 5)}
                    pendingApprovalsCount={
                      approvalsLoaded ? approvalsCount : pendingExpenses.length
                    }
                    employeeBalances={employeeBalances}
                    onApproveExpense={handleApproveExpense}
                    onRejectExpense={handleRejectExpense}
                    approvalActionLoadingById={approvalActionLoadingById}
                    onAllocateFunds={handleAllocateFunds}
                    onUpdateSpendableLimit={handleUpdateSpendableLimit}
                    loadingTeamAllocations={teamAllocationsLoading}
                    loadingApprovals={
                      isOwner ? approvalsLoading : myRequestedApprovalsLoading
                    }
                    onExecute={handleExecutePayment}
                    executeLoadingById={executeLoadingById}
                  />
                )}

                {activeTab === "team" && (
                  <PettyCashAllocations
                    employeeBalances={employeeBalances}
                    onAllocateFunds={handleAllocateFunds}
                    onUpdateSpendableLimit={handleUpdateSpendableLimit}
                    loadingTeamAllocations={teamAllocationsLoading}
                  />
                )}

                {activeTab === "submit" && (
                  <PettyCashExpenses
                    employees={employees}
                    onAddExpense={handleAddExpense}
                  />
                )}

                {activeTab === "approvals" && (
                  <PettyCashApprovals
                    allApprovals={allApprovalsMapped}
                    pendingAllocations={pendingAllocations}
                    pendingApprovalsCount={
                      approvalsLoaded ? approvalsCount : pendingExpenses.length
                    }
                    onApproveExpense={handleApproveExpense}
                    onRejectExpense={handleRejectExpense}
                    approvalActionLoadingById={approvalActionLoadingById}
                    onApproveAllocation={handleApproveAllocation}
                    onRejectAllocation={handleRejectAllocation}
                    loadingApprovals={approvalsLoading}
                  />
                )}

                {activeTab === "execute" && (
                  <PettyCashExecute
                    approvals={myRequestedApprovals}
                    loadingApprovals={myRequestedApprovalsLoading}
                    onExecute={handleExecutePayment}
                    executeLoadingById={executeLoadingById}
                  />
                )}

                {activeTab === "history" && (
                  <PettyCashReports expenses={expenses} employees={employees} />
                )}
              </div>
            </>
          )}

          <AddPettyCashDialog
            open={isAddPettyCashOpen}
            onOpenChange={setIsAddPettyCashOpen}
            onSubmit={handleTopUp}
            title="Add Petty Cash"
            description="Top up your petty cash wallet. This will be available for allocation to team members."
            submitLabel="Add Funds"
            showRemarks={true}
            remarksLabel="Remarks (optional)"
            remarksPlaceholder="Monthly top-up"
          />

          <AddPettyCashDialog
            open={isCreateWalletOpen}
            onOpenChange={setIsCreateWalletOpen}
            onSubmit={handleCreateWallet}
            title="Create Wallet"
            description="Create a petty cash wallet by adding an initial amount."
            submitLabel="Create Wallet"
            showRemarks={false}
          />
        </div>
      </div>
    </LoadingScreen>
  );
}
